// src/shared/components/DateField.tsx

import { useMemo } from 'react';
import { TextInput, type MantineSize, type StyleProp } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';

export interface DateFieldProps {
  label?: string;
  'aria-label'?: string;
  /** 'yyyy-MM-dd' or null. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** 'yyyy-MM-dd' bounds (inclusive). */
  minDate?: string;
  maxDate?: string;
  size?: MantineSize;
  w?: StyleProp<React.CSSProperties['width']>;
  disabled?: boolean;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Calendar-day entry that is safe on phones: the Mantine DatePickerInput on
 * fine-pointer devices, a Mantine-styled native input[type="date"] on
 * coarse-pointer ones (one tap opens the OS date wheel). Same reasoning and
 * detection as DateTimeField; values are 'yyyy-MM-dd' strings either way, so
 * no timezone can sneak in.
 */
export function DateField({ label, value, onChange, minDate, maxDate, size = 'sm', w, disabled, ...rest }: DateFieldProps) {
  const isCoarsePointer = useMemo(() => window.matchMedia('(pointer: coarse)').matches, []);

  if (!isCoarsePointer) {
    return (
      <DatePickerInput
        label={label}
        aria-label={rest['aria-label']}
        value={value}
        onChange={(next) => onChange(typeof next === 'string' && DATE_RE.test(next) ? next : null)}
        minDate={minDate}
        maxDate={maxDate}
        size={size}
        w={w}
        disabled={disabled}
        valueFormat="ddd, MMM D, YYYY"
        popoverProps={{ withinPortal: true }}
      />
    );
  }

  return (
    <TextInput
      type="date"
      label={label}
      aria-label={rest['aria-label']}
      value={value ?? ''}
      onChange={(e) => {
        const v = e.currentTarget.value;
        onChange(DATE_RE.test(v) ? v : null);
      }}
      min={minDate}
      max={maxDate}
      size={size}
      w={w}
      disabled={disabled}
    />
  );
}
